import type { MetaDescriptor } from 'react-router';
import type { Graph } from 'schema-dts';

export const createLDSchema = (
    schemas: Array<MetaDescriptor[] | undefined>,
): MetaDescriptor[] => {
    return schemas
        .filter(
            (schema): schema is MetaDescriptor[] =>
                !!schema && schema.length > 0,
        )
        .map((schema) => {
            return {
                'script:ld+json': {
                    '@context': 'http://schema.org',
                    ...schema?.[0],
                },
            };
        });
};

const isLDSchema = (
    tag: MetaDescriptor,
): tag is { 'script:ld+json': Graph } => {
    return 'script:ld+json' in tag;
};

export const mergeLDSchema = (
    mergedMeta: MetaDescriptor[],
): MetaDescriptor[] => {
    const unwrapped = mergedMeta
        .filter(isLDSchema)
        // Nested routes merge more than once (layout meta, then index meta), so
        // an entry may already be a merged `@graph` document — unwrap it back to
        // its nodes; a typeless `@graph`-in-`@graph` wrapper breaks LD parsers.
        .flatMap((tag) => {
            const schema = tag['script:ld+json'];
            return typeof schema === 'object' &&
                schema !== null &&
                '@graph' in schema
                ? schema['@graph']
                : [schema];
        });

    // Every merge pass appends a breadcrumb trail, so nested routes end up
    // with one per pass — keep only the last (leaf-most) trail.
    const isBreadcrumb = (node: (typeof unwrapped)[number]) =>
        typeof node === 'object' &&
        node !== null &&
        '@type' in node &&
        node['@type'] === 'BreadcrumbList';
    const lastBreadcrumb = [...unwrapped].reverse().find(isBreadcrumb);
    const schemasToMerge = unwrapped.filter(
        (node) => !isBreadcrumb(node) || node === lastBreadcrumb,
    );

    const mergedSchema = {
        'script:ld+json': {
            '@context': 'http://schema.org',
            '@graph': schemasToMerge,
        },
    };

    // Remove all other ld+schema
    const removeLDSchema = mergedMeta.filter((tag) => !isLDSchema(tag));

    // Return mergedMeta, with a single mergedSchema

    // Temp fix for hydration. Don't add any ld-json schema
    return removeLDSchema.concat(mergedSchema);
};
